import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  OnInit,
  computed,
  effect,
  inject,
  input,
  signal,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { TranslateModule } from '@ngx-translate/core';
import { CookieService } from 'ngx-cookie-service';

import { ButtonComponent } from '@libs/front/components/button/button.component';
import { SelectComponent, SelectOption as LibSelectOption } from '@libs/front/components/select/select.component';

import { VibrationData } from 'src/app/bo/vibrationsForChart/vibration-data';
import { PPV } from 'src/app/bo/vibrationsForChart/ppv';
import { VibrationChartChannel } from 'src/app/bo/vibrationsForChart/vibration-chart-channel';
import { Ponderation } from 'src/app/bo/ponderation.model';
import { Standard } from 'src/app/bo/vibrationsForChart/standard';
import { StandardGraphDataDto } from 'src/app/bo/vibrationsForChart/standard-graph-data';
import { VibrationsRecordService } from 'src/app/services/vibrations-record.service';
import { FormatTransducerTypePipe } from 'src/app/pipes/format-transducer-type.pipe';
import { Chart } from 'src/app/utils/chart-config';

type ChartTab = 'signal' | 'frequency' | 'standard';

/**
 * Refonte de `app-vibration-chart` : même comportement (mêmes graphes Chart.js, même
 * chargement des normes, même export de rapport), rendu avec les composants génériques
 * de `@libs/front/components`.
 */
@Component({
  selector: 'app-blast-vibration-chart',
  templateUrl: './blast-vibration-chart.component.html',
  styleUrls: ['./blast-vibration-chart.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    TranslateModule,
    ButtonComponent,
    SelectComponent,
    FormatTransducerTypePipe,
  ],
})
export class BlastVibrationChartComponent implements OnInit {
  readonly siteId = input<number>();
  readonly eventId = input<number>();
  readonly tirName = input<string>();
  readonly siteName = input<string>();
  readonly vibrationRecord = input<VibrationData>();
  readonly ponderationTypes = input<Ponderation[]>([]);

  protected readonly selectedTab = signal<ChartTab>('signal');
  protected readonly localRecord = signal<VibrationData | null>(null);
  protected readonly isDownloadingReport = signal(false);
  protected readonly standards = signal<Standard[]>([]);
  protected readonly selectedStandardCode = signal<string>('');
  protected readonly standardGraphData = signal<StandardGraphDataDto | null>(null);
  protected readonly selectedPonderationId = signal<number | null>(null);

  protected readonly channelNames = ['VIBRATION.VERTICAL', 'VIBRATION.LONGITUDE', 'VIBRATION.TRANSVERSE'];

  protected readonly tabs: { key: ChartTab; label: string }[] = [
    { key: 'signal', label: 'VIBRATION.SIGNAL' },
    { key: 'frequency', label: 'VIBRATION.FREQUENCY' },
    { key: 'standard', label: 'VIBRATION.STANDARD' },
  ];

  protected readonly channels = computed(() => this.vibrationRecord()?.signalDataEntity?.signalData?.channels ?? []);

  /** Première option = réinitialisation, équivalent du `clearable` de l'ancien ng-select. */
  protected readonly standardOptions = computed<LibSelectOption[]>(() => [
    { id: '', label: '—' },
    ...this.standards().map(s => ({ id: s.code, label: s.country ? `${s.code} — ${s.country}` : s.code })),
  ]);

  private chart: Chart | null = null;
  private standardChart: Chart | null = null;

  private readonly vibrationsRecordService = inject(VibrationsRecordService);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly cookieService = inject(CookieService);
  private readonly el = inject(ElementRef);

  constructor() {
    effect(() => {
      const record = this.vibrationRecord();
      if (record) {
        this.localRecord.set(record);
        setTimeout(() => {
          this.drawingVibrations(record);
          this.drawFFTChart(record);
        }, 500);
      }
    });
  }

  ngOnInit(): void {
    this.vibrationsRecordService.getAllStandards().subscribe({
      next: (response) => {
        this.standards.set(response.data ?? []);
        this.cdr.detectChanges();
      },
    });
  }

  protected setTab(tab: ChartTab): void {
    this.selectedTab.set(tab);
  }

  // ── Rapport ────────────────────────────────────────────────────────────────

  protected downloadRecordReport(): void {
    const recordId = this.vibrationRecord()?.id;
    if (!recordId || this.isDownloadingReport()) {
      return;
    }

    this.isDownloadingReport.set(true);
    const language = this.cookieService.get('epcLanguage') || 'en';

    this.vibrationsRecordService.downloadRecordReport(recordId, language, this.tirName() || '', this.siteName() || '', this.siteId()).subscribe({
      next: (response) => {
        const blob = response.body ?? new Blob([], { type: 'application/pdf' });
        const fileName = this.getDownloadedFileName(response.headers.get('content-disposition'))
          || `vibration_record_${recordId}_report.pdf`;
        const url = window.URL.createObjectURL(blob);
        const anchor = document.createElement('a');
        anchor.href = url;
        anchor.download = fileName;
        document.body.appendChild(anchor);
        anchor.click();

        setTimeout(() => {
          anchor.remove();
          window.URL.revokeObjectURL(url);
          this.isDownloadingReport.set(false);
          this.cdr.detectChanges();
        }, 100);
      },
      error: (error) => {
        console.error('Error downloading vibration record report:', error);
        this.isDownloadingReport.set(false);
        this.cdr.detectChanges();
      },
    });
  }

  private getDownloadedFileName(contentDisposition: string | null): string | null {
    if (!contentDisposition) {
      return null;
    }

    const utf8FileName = /filename\*=UTF-8''([^;]+)/i.exec(contentDisposition);
    if (utf8FileName?.[1]) {
      return decodeURIComponent(utf8FileName[1]);
    }

    const fileName = /filename="?([^";]+)"?/i.exec(contentDisposition);
    return fileName?.[1] ?? null;
  }

  // ── Pondération ────────────────────────────────────────────────────────────

  protected onPonderationChange(event: any): void {
    const ponderationId = event?.ponderationId ?? null;
    this.selectedPonderationId.set(ponderationId);
    const record = this.localRecord();
    if (ponderationId && this.siteId() && record?.id) {
      this.vibrationsRecordService
        .GetRecordById(this.siteId()!, record.id, ponderationId)
        .subscribe({
          next: (response) => {
            this.localRecord.set(response.data);
            this.cdr.detectChanges();
          },
          error: (err) => {
            console.error('Error fetching record with ponderation:', err);
          },
        });
    } else {
      this.localRecord.set(record);
      this.cdr.detectChanges();
    }
  }

  // ── Helpers d'affichage ────────────────────────────────────────────────────

  protected abs(value: number): number {
    return Math.abs(value);
  }

  public findMaxAbsValue(min: number = 0, max: number = 0): number {
    return Math.abs(max) > Math.abs(min) ? max : Math.abs(min);
  }

  public findPPVMax(ppvs?: PPV[]): number {
    if (!ppvs || ppvs.length === 0) return 0;
    return Math.max(...ppvs.flatMap(ppv => [Math.abs(ppv.ppvMax), Math.abs(ppv.ppvMin)]));
  }

  // ── Graphes « Signal » ─────────────────────────────────────────────────────

  private drawingVibrations(vibrationsData: VibrationData): void {
    const minMax = this.findMinMax(vibrationsData);
    vibrationsData?.signalDataEntity?.signalData?.channels.forEach((chanel, indexChanel) => {
      if (!this.checkChannelData(chanel)) {
        this.drawingChannel(indexChanel, vibrationsData, minMax);
      }
    });
  }

  private drawingChannel(index: number, vibrations: VibrationData, minMax: number[]): void {
    const canvas = this.el.nativeElement.querySelector('#canvas-' + index) as HTMLCanvasElement;
    if (!canvas) {
      console.warn(`Canvas with ID 'canvas-${index}' not found.`);
      return;
    }

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const rawData = vibrations?.signalDataEntity?.signalData?.channels[index]?.timeSpectrum?.values || [];
    if (!rawData.length) {
      console.warn('No data available for channel', index);
      return;
    }

    const metadata = vibrations?.recordMetadata;
    const isBargraph = (metadata as any)?.recordType === 'Bargraph';
    const samplingFrequency = metadata?.samplingPeriod;

    let data: { x: number; y: number }[] = [];
    let totalDurationMs = 0;

    if (isBargraph) {
      if ((metadata as any)?.timestamp && (metadata as any)?.endDate) {
        const start = new Date((metadata as any).timestamp).getTime();
        const end = new Date((metadata as any).endDate).getTime();
        totalDurationMs = end - start;
      } else if ((metadata as any)?.recordLength && (metadata as any).recordLength > 0) {
        totalDurationMs = (metadata as any).recordLength * 1000;
      } else {
        console.error('No duration info for bargraph (endDate & recordLength missing)');
        return;
      }

      const step = totalDurationMs / rawData.length;
      data = rawData.map((value, idx) => ({
        x: idx * step,
        y: Math.abs(value as number),
      }));
    } else {
      if (!samplingFrequency || samplingFrequency <= 0) {
        console.error('Invalid sampling frequency:', samplingFrequency);
        return;
      }

      data = rawData.map((value, idx) => ({
        x: (idx / samplingFrequency) * 1000,
        y: value as number,
      }));

      totalDurationMs = (rawData.length / samplingFrequency) * 1000;
    }

    const maxAbsValue = Math.max(...data.map(d => Math.abs(d.y)));
    const maxIndex = data.findIndex(d => Math.abs(d.y) === maxAbsValue);
    const actualMaxValue = data[maxIndex]?.y ?? 0;
    const maxXValue = data[maxIndex]?.x ?? 0;

    const isMicroChannel = index > 2;

    let sharedAbs: number;
    if (isMicroChannel) {
      const ownMin = vibrations?.signalDataEntity?.signalData?.channels[index]?.timeSpectrum?.ppvMin ?? 0;
      const ownMax = vibrations?.signalDataEntity?.signalData?.channels[index]?.timeSpectrum?.ppvMax ?? 0;
      sharedAbs = Math.max(Math.abs(ownMin), Math.abs(ownMax), maxAbsValue) || 1;
    } else {
      sharedAbs = Math.max(Math.abs(minMax[0]), Math.abs(minMax[1]), maxAbsValue) || 1;
    }

    const yBound = Math.max(1, Math.ceil(sharedAbs));
    const yMin = isBargraph ? 0 : -yBound;
    const yMax = yBound;
    const yRange = yMax - yMin;
    const yStep = Math.max(0.1, Number((yRange / 8).toFixed(2)));
    const xStep = totalDurationMs > 0 ? totalDurationMs / 10 : 1;

    const startTimestampMs = (metadata as any)?.timestamp ? new Date((metadata as any).timestamp).getTime() : 0;

    new Chart(ctx, {
      type: 'line',
      data: {
        datasets: [{
          label: 'Channel #' + (index + 1),
          data: data,
          fill: false,
          tension: 0.1,
          borderWidth: 1,
          borderColor: 'rgb(153,0,77)',
        }],
      },
      options: {
        elements: {
          point: { radius: 0 },
        },
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          legend: { display: false },
          annotation: {
            annotations: [{
              type: 'point',
              xValue: maxXValue,
              yValue: actualMaxValue,
              radius: 5,
              borderColor: 'rgb(153,0,77)',
              borderWidth: 1,
            }],
          },
          tooltip: {
            callbacks: isBargraph ? {
              title: (items: any[]) => {
                if (!items?.length) return '';
                const ms = Number(items[0].parsed.x);
                const d = new Date(startTimestampMs + ms);
                const pad = (n: number) => String(n).padStart(2, '0');
                return `${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
              },
            } : {},
          },
        },
        scales: {
          x: {
            type: 'linear',
            min: 0,
            max: totalDurationMs,
            ticks: {
              stepSize: xStep,
              callback: function (val) {
                const ms = Number(val);
                if (isBargraph && startTimestampMs) {
                  const d = new Date(startTimestampMs + ms);
                  const pad = (n: number) => String(n).padStart(2, '0');
                  return totalDurationMs >= 3600000
                    ? `${pad(d.getHours())}:${pad(d.getMinutes())}`
                    : `${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
                }
                if (ms >= 3600000) return (ms / 3600000).toFixed(1) + ' h';
                if (ms >= 60000) return (ms / 60000).toFixed(1) + ' min';
                if (ms >= 1000) return (ms / 1000).toFixed(1) + ' s';
                return ms.toFixed(0) + ' ms';
              },
            },
          },
          y: {
            min: yMin,
            max: yMax,
            ticks: {
              stepSize: yStep,
              precision: 2,
            },
            title: {
              display: true,
              text: isMicroChannel ? 'Pa' : 'mm / s',
            },
          },
        },
      },
    });
  }

  // ── Graphe « Frequency » (FFT) ─────────────────────────────────────────────

  private drawFFTChart(vibrations: VibrationData): void {
    if (this.chart) {
      this.chart.destroy();
    }
    const colorsArray = ['rgba(255, 115, 119, 1)', 'rgba(61, 157, 165, 1)', 'rgba(125, 0, 61, 1)', 'rgba(250, 199, 15, 1)'];

    const canvas = this.el.nativeElement.querySelector('#canvas-fft') as HTMLCanvasElement;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const vibrationData: any = this.formatVibrationData(vibrations);

    if (vibrationData == undefined || vibrationData.length == 0) {
      return;
    }

    const filteredChannels = vibrationData.channels.slice(0, 3).map((channel: any) => {
      const filteredFrequencies = [];
      const filteredMagnitudes = [];

      for (let i = 0; i < channel.frequencySpectrum.frequencies.length; i++) {
        if (channel.frequencySpectrum.frequencies[i] >= 0 && channel.frequencySpectrum.frequencies[i] <= 150) {
          filteredFrequencies.push(channel.frequencySpectrum.frequencies[i]);
          filteredMagnitudes.push(channel.frequencySpectrum.magnitudes[i]);
        }
      }

      return {
        name: channel.name,
        frequencySpectrum: {
          frequencies: filteredFrequencies,
          magnitudes: filteredMagnitudes,
        },
      };
    });

    const datasets = filteredChannels.map((channel: any, index: number) => {
      return {
        label: `C${index + 1}`,
        data: channel.frequencySpectrum.magnitudes,
        borderColor: colorsArray[index],
        backgroundColor: colorsArray[index],
        fill: false,
        tension: 0,
        pointRadius: 0,
        borderWidth: 1,
        stepped: false,
      };
    });

    this.chart = new Chart(ctx, {
      type: 'line',
      data: {
        labels: filteredChannels[0]?.frequencySpectrum?.frequencies,
        datasets: datasets,
      },
      options: {
        responsive: true,
        scales: {
          x: {
            title: {
              display: true,
              text: 'Fréquence (Hz)',
            },
          },
          y: {
            title: {
              display: true,
              text: 'Magnitude',
            },
            beginAtZero: true,
          },
        },
        plugins: {
          tooltip: {
            enabled: true,
          },
          legend: {
            position: 'top',
            labels: {
              boxWidth: 10,
              boxHeight: 10,
              padding: 10,
            },
          },
        },
      },
    });
  }

  private checkChannelData(data: VibrationChartChannel): boolean {
    let dataNullNumber = 0;
    data.timeSpectrum.values.forEach((v) => {
      if (v === 0) {
        dataNullNumber++;
      }
    });
    return dataNullNumber === data.timeSpectrum.values.length;
  }

  private findMinMax(vibration: VibrationData): number[] {
    const minMax = [0, 0, 0, 0];
    vibration?.signalDataEntity?.signalData?.channels.forEach((value, index) => {
      if (index < 3) {
        if (value.timeSpectrum.ppvMin < minMax[0]) minMax[0] = value.timeSpectrum.ppvMin;
        if (value.timeSpectrum.ppvMax > minMax[1]) minMax[1] = value.timeSpectrum.ppvMax;
      } else if (index === 3) {
        // channel #4 own min/max (if present)
        minMax[2] = value.timeSpectrum.ppvMin;
        minMax[3] = value.timeSpectrum.ppvMax;
      }
    });
    return minMax;
  }

  private formatVibrationData(Vibration: VibrationData) {
    const channels = [];
    for (let i = 0; i < (Vibration?.signalDataEntity?.signalData?.channels?.length ?? 0); i++) {
      const frequencies = this.vibrationRecord()?.signalDataEntity?.signalData?.channels[i].frequencySpectrum.frequencies;
      const magnitudes = this.vibrationRecord()?.signalDataEntity?.signalData?.channels[i].frequencySpectrum.magnitudes;
      channels.push({
        name: `Channel ${i + 1}`,
        frequencySpectrum: {
          frequencies: frequencies,
          magnitudes: magnitudes,
        },
      });
    }
    return { channels: channels };
  }

  // ── Onglet « Standard » ────────────────────────────────────────────────────

  /** Adapte la sélection d'`app-select` au contrat de `onStandardChange`. */
  protected onStandardSelected(option: LibSelectOption): void {
    const code = (option?.id ?? '') as string;
    this.selectedStandardCode.set(code);
    this.onStandardChange(this.standards().find(s => s.code === code) ?? null);
  }

  protected onStandardChange(standard: Standard | null): void {
    this.standardGraphData.set(null);
    if (this.standardChart) {
      this.standardChart.destroy();
      this.standardChart = null;
    }
    if (!standard || !this.siteId() || !this.vibrationRecord()?.id) return;

    this.vibrationsRecordService.getStandardGraphData(this.siteId()!, this.vibrationRecord()!.id, standard.code).subscribe({
      next: (response) => {
        const data = response.data;
        this.standardGraphData.set(data);
        if (data?.weightedPpvValues) {
          if (this.vibrationRecord() !== null) {
            this.vibrationRecord()!.timeSpectrumWeighted = data.weightedPpvValues as any;
          }
        }
        this.cdr.detectChanges();

        if (!data?.weightedPpvValues && data?.complianceChartData) {
          setTimeout(() => this.drawComplianceChart(), 0);
        }
      },
      error: (err) => console.error('Error loading standard graph data:', err),
    });
  }

  private drawComplianceChart(): void {
    if (this.standardChart) {
      this.standardChart.destroy();
      this.standardChart = null;
    }
    const canvas = this.el.nativeElement.querySelector('#canvas-compliance') as HTMLCanvasElement;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    const chartData = this.standardGraphData()!.complianceChartData!;

    const isXLog = chartData.isLogX;
    const isYLog = chartData.isLogY;
    const toPositive = (value: number, fallback: number): number => {
      if (Number.isFinite(value) && value > 0) return value;
      return fallback;
    };
    const xMin = isXLog ? toPositive(chartData.freqMin, 0.1) : chartData.freqMin;
    const xMax = isXLog ? toPositive(chartData.freqMax, xMin * 10) : chartData.freqMax;
    const yMin = isYLog ? toPositive(chartData.velocityMin, 0.1) : chartData.velocityMin;
    const yMax = isYLog ? toPositive(chartData.velocityMax, yMin * 10) : chartData.velocityMax;
    const sanitizePoint = (x: number, y: number): boolean => {
      if (isXLog && x <= 0) return false;
      if (isYLog && y <= 0) return false;
      return true;
    };

    const datasets: any[] = chartData.criteriaLines.map((line) => ({
      label: line.name,
      data: line.points
        .filter(p => sanitizePoint(p.x, p.y))
        .map(p => ({ x: p.x, y: p.y })),
      borderColor: line.color,
      backgroundColor: line.color,
      borderWidth: 2.5,
      borderDash: line.isDashed ? [6, 4] : [],
      pointRadius: 0,
      fill: false,
      tension: 0,
    }));

    const channelShapes = ['circle', 'rectRot', 'rect'];

    // Même garde que le rapport, qui écarte un point qu'il ne peut pas placer.
    const measuredPoints = (chartData.measuredPoints ?? [])
      .filter(point => point.frequency > 0 && point.ppv >= 0 && sanitizePoint(point.frequency, point.ppv));

    // Un enregistrement bargraph donne une somme vectorielle par fréquence : elles forment un seul
    // nuage, pas une série chacune, sinon la légende aurait une entrée par fréquence. Le nuage est
    // tracé pour toutes les normes ; c'est l'API qui décide s'il remplace les points par canal.
    // Même règle que le rapport.
    const vectorSumPoints = measuredPoints.filter(point => point.isVectorSum);
    if (vectorSumPoints.length > 0) {
      const color = vectorSumPoints[0].color || '#990033';
      const maxPpv = Math.max(...vectorSumPoints.map(point => point.ppv));
      datasets.push({
        label: `${vectorSumPoints[0].channelName} (max ${maxPpv.toFixed(2)} mm/s)`,
        data: vectorSumPoints.map(point => ({ x: point.frequency, y: point.ppv })),
        borderColor: color,
        backgroundColor: color,
        pointStyle: 'circle',
        pointRadius: 3,
        pointHoverRadius: 6,
        showLine: false,
        isChannel: true,
      });
    }

    // La forme suit le numéro de canal, pas la position dans la liste : un canal sans fréquence ou
    // PPV exploitable est absent, et indexer par position décalerait les autres. La couleur vient
    // de l'API, qui sert celle du rapport pour ce canal.
    measuredPoints.filter(point => !point.isVectorSum).forEach((point) => {
      const channelIndex = Math.max(point.channelNumber - 1, 0);
      datasets.push({
        label: point.channelName,
        data: [{ x: point.frequency, y: point.ppv }],
        borderColor: point.color,
        backgroundColor: point.color,
        pointStyle: channelShapes[channelIndex] ?? 'circle',
        pointRadius: 4,
        pointHoverRadius: 7,
        showLine: false,
        isChannel: true,
      });
    });

    this.standardChart = new Chart(ctx, {
      type: 'scatter',
      data: { datasets },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        interaction: {
          mode: 'nearest',
          intersect: false,
          axis: 'xy',
        },
        plugins: {
          legend: {
            position: 'top',
            labels: {
              boxWidth: 12,
              filter: (item: any, data: any) => {
                return !!data.datasets[item.datasetIndex]?.isChannel;
              },
            },
            onClick: (_e: any, legendItem: any, legend: any) => {
              const ci = legend.chart;
              ci.data.datasets.forEach((ds: any, idx: number) => {
                if (ds.label === legendItem.text) {
                  ci.setDatasetVisibility(idx, !ci.isDatasetVisible(idx));
                }
              });
              ci.update();
            },
          },
          tooltip: {
            enabled: true,
            callbacks: {
              title: (items: any[]) => {
                if (!items?.length) return '';
                const ds = items[0].chart.data.datasets[items[0].datasetIndex];
                return ds.label ?? '';
              },
              label: (item: any) => {
                const ds = item.chart.data.datasets[item.datasetIndex];
                if (ds.isChannel) {
                  // Le nuage de somme vectorielle tient plusieurs points dans un seul dataset :
                  // on lit le point survolé, pas toujours le premier.
                  const raw = ds.data?.[item.dataIndex] ?? {};
                  const frequency = raw.x ?? item.parsed.x;
                  const ppv = raw.y ?? item.parsed.y;
                  return [`Frequency: ${Number(frequency).toFixed(2)} Hz`, `PPV: ${Number(ppv).toFixed(2)} mm/s`];
                }
                return '';
              },
            },
          },
        },
        scales: {
          x: {
            type: isXLog ? 'logarithmic' : 'linear',
            min: xMin,
            max: xMax,
            title: { display: true, text: 'Frequency (Hz)' },
            ticks: {
              callback: (val: number) => {
                if (!isXLog) return `${val} Hz`;
                const tick = Number(val);
                if (!Number.isFinite(tick) || tick <= 0) return null;
                const power = Math.floor(Math.log10(tick));
                const normalized = tick / Math.pow(10, power);
                return normalized === 1 || normalized === 5 ? `${tick} Hz` : null;
              },
            },
          },
          y: {
            type: isYLog ? 'logarithmic' : 'linear',
            min: yMin,
            max: yMax,
            title: { display: true, text: 'Velocity (mm/s)' },
            ticks: {
              autoSkip: !isYLog,
              callback: (val: number) => {
                if (!isYLog) return `${val}`;
                const tick = Number(val);
                if (!Number.isFinite(tick) || tick <= 0) return null;
                const power = Math.floor(Math.log10(tick));
                const normalized = tick / Math.pow(10, power);
                return normalized === 1 || normalized === 5 ? `${tick}` : null;
              },
            },
          },
        },
        showLine: true,
      } as any,
    });
  }
}
